import type { SystemMetric } from "./types/api";

/** How the API merges one-minute samples into a chart bucket. */
export type Aggregate = "avg" | "max" | "sum";

export type MetricId =
  | "cpu"
  | "memory"
  | "disk"
  | "network"
  | "load"
  | "swap"
  | "paging"
  | "conntrack"
  | "tableFull"
  | "retrans"
  | "overflows"
  | "timewait";

export interface MetricDefinition {
  id: MetricId;
  title: string;
  unit: string;
  digits: number;
  /**
   * What a bucket of this metric means. The API decides it (usage gauges average, stress
   * indicators keep their peak, event counters add up); this only picks the right words
   * for the summary line.
   */
  aggregate: Aggregate;
  series: ReadonlyArray<{ metric: SystemMetric; name: string }>;
}

export const METRICS: Record<MetricId, MetricDefinition> = {
  cpu: {
    id: "cpu",
    title: "CPU 使用率",
    unit: "%",
    digits: 1,
    aggregate: "avg",
    series: [{ metric: "cpu_percent", name: "CPU" }]
  },
  memory: {
    id: "memory",
    title: "内存使用率",
    unit: "%",
    digits: 1,
    aggregate: "avg",
    series: [{ metric: "memory_percent", name: "内存" }]
  },
  disk: {
    id: "disk",
    title: "磁盘使用率",
    unit: "%",
    digits: 1,
    aggregate: "avg",
    series: [{ metric: "disk_percent", name: "磁盘" }]
  },
  network: {
    id: "network",
    title: "网络吞吐",
    unit: "kbit/s",
    digits: 0,
    aggregate: "avg",
    series: [
      { metric: "rx_kbps", name: "接收" },
      { metric: "tx_kbps", name: "发送" }
    ]
  },
  load: {
    id: "load",
    title: "系统负载",
    unit: "",
    digits: 2,
    aggregate: "avg",
    series: [{ metric: "load1", name: "1 分钟负载" }]
  },
  swap: {
    id: "swap",
    title: "Swap 使用率",
    unit: "%",
    digits: 1,
    aggregate: "avg",
    series: [{ metric: "swap_percent", name: "Swap" }]
  },
  paging: {
    id: "paging",
    title: "Swap 换页",
    unit: "KiB/s",
    digits: 0,
    aggregate: "max",
    series: [
      { metric: "swap_in_kib_s", name: "换入" },
      { metric: "swap_out_kib_s", name: "换出" }
    ]
  },
  conntrack: {
    id: "conntrack",
    title: "连接跟踪表使用率",
    unit: "%",
    digits: 2,
    aggregate: "max",
    series: [{ metric: "conntrack_percent", name: "使用率" }]
  },
  tableFull: {
    id: "tableFull",
    title: "连接跟踪表满事件",
    unit: "次",
    digits: 0,
    aggregate: "sum",
    series: [{ metric: "conntrack_table_full_events", name: "table full" }]
  },
  retrans: {
    id: "retrans",
    title: "TCP 重传率",
    unit: "%",
    digits: 2,
    aggregate: "max",
    series: [{ metric: "tcp_retrans_percent", name: "重传率" }]
  },
  overflows: {
    id: "overflows",
    title: "TCP 队列溢出与内存压力",
    unit: "次",
    digits: 0,
    aggregate: "sum",
    series: [
      { metric: "tcp_listen_overflows", name: "accept 队列溢出" },
      { metric: "tcp_timewait_overflows", name: "TIME_WAIT 溢出" },
      { metric: "tcp_memory_pressures", name: "内存压力" }
    ]
  },
  timewait: {
    id: "timewait",
    title: "TIME_WAIT 套接字",
    unit: "个",
    digits: 0,
    aggregate: "max",
    series: [{ metric: "tcp_timewait_count", name: "套接字" }]
  }
};

/** Series colours in assignment order; a chart never has more series than this. */
export const SERIES_COLORS = ["var(--series-1)", "var(--series-2)", "var(--series-3)"] as const;
